import { useStore } from '@nanostores/react';
import { reloadPreview as webcontainerReloadPreview } from '@webcontainer/api';
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { LivePreviewDropdown } from './LivePreviewDropdown';
import { PortDropdown } from './PortDropdown';
import Resizer from './Resizer';
import { LegacyIconButton } from '~/components/ui/LegacyIconButton';
import { LoadingScreen } from '~/components/workbench/LoadingScreen';
import { snapshotManager } from '~/lib/snapshots/snapshot-manager';
import { setPreviewElement } from '~/lib/stores/device-preview';
import { userStore } from '~/lib/stores/user';
import { workbenchStore } from '~/lib/stores/workbench';
import { webcontainer } from '~/lib/webcontainer';
import { classNames } from '~/utils/classNames';

interface PreviewProps {
  login?: () => true | Promise<boolean>;
}

export interface PreviewRef {
  reload(): Promise<void>;
}

const FEATURES = [
  'magnetometer',
  'accelerometer',
  'gyroscope',
  'geolocation',
  'microphone',
  'camera',
  'payment',
  'autoplay',
  'serial',
  'xr-spatial-tracking',
  'cross-origin-isolated',
];

enum PreviewLoadState {
  Idle,
  PossiblyWaitingForConnect,
  Ready,
}

const POLICY = ((document as any).featurePolicy?.allowedFeatures() ?? FEATURES).join('; ');

const PreviewImpl = forwardRef<PreviewRef, PreviewProps>(({ login }, ref) => {
  const framework = useStore(workbenchStore.framework);
  const isReady = useStore(workbenchStore.previewsStore.isReady);
  const previews = useStore(workbenchStore.previews);
  const activePreviewIndex = useStore(workbenchStore.previewsStore.activePreviewIndex);
  const featureFlags = useStore(userStore.featureFlags);
  const activePreview = previews[activePreviewIndex];

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [isPortDropdownOpen, setIsPortDropdownOpen] = useState(false);
  const [pathname, setPathname] = useState('');
  const [iframeUrl, setIframeUrl] = useState<string | undefined>();
  const [loadState, setLoadState] = useState(PreviewLoadState.Idle);

  const [previewElement] = useState(() => {
    const element = document.createElement('div');

    element.classList.add('fixed', 'inset-0', 'z-max');

    return element;
  });

  const [mobileWidth, setMobileWidth] = useState(320);
  const [isResponsiveMode, setIsResponsiveMode] = useState(() => workbenchStore.framework.get()?.name === 'expo');
  const isInspectorOpen = useStore(workbenchStore.isInspectorOpen);
  const [isPreviewFullscreen, setIsPreviewFullscreen] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const isLoadingSnapshot = useStore(snapshotManager.isLoadingSnapshot);

  useEffect(() => {
    const unsubscribeFromFramework = workbenchStore.framework.subscribe((framework) => {
      if (framework?.name === 'expo') {
        setIsResponsiveMode(true);

        // unsubscribe from the framework once responsive mode is enabled, as this is an initialization step
        queueMicrotask(() => {
          unsubscribeFromFramework();
        });
      }
    });

    return () => {
      unsubscribeFromFramework();
    };
  }, []);

  useEffect(() => {
    if (!activePreview) {
      setPathname('');
      setIframeUrl(undefined);

      return;
    }

    const { baseUrl } = activePreview;
    const { pathname } = new URL(activePreview.baseUrl);

    setPathname(pathname);
    setIframeUrl(baseUrl);
  }, [activePreview]);

  useEffect(() => {
    if (loadState !== PreviewLoadState.PossiblyWaitingForConnect) {
      return undefined;
    }

    /**
     * @todo This is just in-case the second event never fires. This can only happen when
     * the SW takes ages to boot up.
     *
     * Once https://github.com/stackblitz/webcontainer/pull/1571 lands we can remove this.
     */
    const timeout = setTimeout(() => setLoadState(PreviewLoadState.Ready), 5000);

    return () => clearTimeout(timeout);
  }, [loadState]);

  useEffect(() => {
    workbenchStore.previewsStore.ref = iframeRef.current;
  });

  useImperativeHandle(ref, () => {
    return {
      async reload() {
        if (iframeRef.current) {
          await webcontainerReloadPreview(iframeRef.current);
        }
      },
    };
  }, []);

  const toggleResponsiveMode = () => {
    setIsResponsiveMode(!isResponsiveMode);
  };

  const toggleInspector = useCallback(async () => {
    if (!(await login?.())) {
      return;
    }

    const isInspectorOpen = workbenchStore.isInspectorOpen.get();
    const newValue = !isInspectorOpen;

    workbenchStore.isInspectorOpen.set(newValue);

    import('modern-screenshot/dist/index.mjs?raw').then(async ({ default: modernScreenshotRaw }) => {
      const base64ModernScreenshot = `data:text/javascript;base64,${btoa(modernScreenshotRaw)}`;
      const instance = await webcontainer;

      instance.internal.previewScript.storage.set(
        newValue ? { inspect: newValue, base64ModernScreenshot } : { inspect: newValue },
      );
    });
  }, [login]);

  const toggleFullScreen = () => {
    if (!iframeRef.current) {
      return;
    }

    setShowOverlay(true);

    requestAnimationFrame(() => {
      setIsPreviewFullscreen((value) => {
        const newValue = !value;

        if (newValue) {
          document.body.appendChild(previewElement);
        } else {
          document.body.removeChild(previewElement);
        }

        return newValue;
      });

      setTimeout(() => {
        setShowOverlay(false);
      }, 100);
    });
  };

  const reloadPreview = () => {
    if (iframeRef.current) {
      webcontainerReloadPreview(iframeRef.current).catch(() => {
        // ignore errors
      });
    }
  };

  const openPreviewInSeparateTab = async () => {
    const url = await workbenchStore.previewsStore.getTunnelURL();

    // we fallback to the iframe URL if the tunnel URL is not available
    window.open(url ?? iframeUrl, '_blank');
  };

  const handleAddressChange = (event: KeyboardEvent) => {
    if (activePreview && event.key === 'Enter') {
      setPathname((currentPathname) => {
        let newPathname = currentPathname;

        // normalize the pathname
        if (/^(?:www.|https?:\/\/)/.test(currentPathname)) {
          newPathname = '/';
        } else if (!currentPathname.startsWith('/')) {
          newPathname = `/${currentPathname}`;
        }

        try {
          const newUrl = new URL(activePreview.baseUrl + newPathname.replace(/^\/+/, '/'));

          setIframeUrl(newUrl.toString());
        } catch {
          newPathname = '/';
          setIframeUrl(activePreview.baseUrl);
        }

        if (inputRef.current) {
          inputRef.current.blur();
        }

        return newPathname;
      });
    }
  };

  const previewMarkup = (
    <div className="size-full flex flex-col bg-bolt-elements-background-depth-2">
      {isPortDropdownOpen && (
        <div className="z-iframe-overlay size-full absolute" onClick={() => setIsPortDropdownOpen(false)} />
      )}
      <div className="bg-bolt-elements-background-depth-2 p-2 flex items-center min-h-0 gap-1.5">
        <LegacyIconButton title="Reload preview" icon="i-ph:arrow-clockwise" onClick={reloadPreview} />
        <div
          className="group flex items-center gap-1 flex-grow bg-bolt-elements-preview-addressBar-background border border-bolt-elements-borderColor text-bolt-elements-preview-addressBar-text rounded-full pl-1 pr-3 py-1 text-sm hover:bg-bolt-elements-preview-addressBar-backgroundHover hover:focus-within:bg-bolt-elements-preview-addressBar-backgroundActive focus-within:bg-bolt-elements-preview-addressBar-backgroundActive
        focus-within-border-bolt-elements-borderColorActive focus-within:text-bolt-elements-preview-addressBar-textActive"
        >
          <div className="flex gap-1.5 w-full">
            {activePreview && (
              <PortDropdown
                activePreviewIndex={activePreviewIndex}
                isDropdownOpen={isPortDropdownOpen}
                setIsDropdownOpen={setIsPortDropdownOpen}
                previews={previews}
              />
            )}
            <input
              ref={inputRef}
              className="w-full bg-transparent outline-none"
              type="text"
              value={pathname}
              onChange={(event) => {
                setPathname(event.target.value);
              }}
              onKeyDown={handleAddressChange}
            />
          </div>
        </div>
        {featureFlags?.previewTunneling ? (
          <LivePreviewDropdown openPreviewInSeparateTab={openPreviewInSeparateTab} iframeUrl={iframeUrl} />
        ) : (
          <LegacyIconButton
            title="Open preview in a separate tab"
            icon="i-ph:arrow-square-out"
            disabled={!iframeUrl}
            onClick={openPreviewInSeparateTab}
          />
        )}

        <LegacyIconButton
          title={isResponsiveMode ? 'Exit responsive mode' : 'Responsive mode'}
          icon="i-ph:devices"
          disabled={!activePreview}
          className={classNames(
            isResponsiveMode
              ? 'text-bolt-elements-item-contentAccent enabled:hover:text-bolt-elements-item-contentAccent'
              : 'text-bolt-elements-textSecondary enabled:hover:text-bolt-elements-textPrimary',
          )}
          onClick={toggleResponsiveMode}
        />
        <LegacyIconButton
          title="Inspector"
          icon="i-ph:cursor-click"
          className={classNames(
            isInspectorOpen
              ? 'text-bolt-elements-item-contentAccent enabled:hover:text-bolt-elements-item-contentAccent'
              : 'text-bolt-elements-textSecondary enabled:hover:text-bolt-elements-textPrimary',
          )}
          onClick={toggleInspector}
        />
        <LegacyIconButton
          className="text-bolt-elements-textSecondary enabled:hover:text-bolt-elements-textPrimary"
          title={isPreviewFullscreen ? 'Exit full screen' : 'Full screen'}
          icon={isPreviewFullscreen ? 'i-ph:corners-in' : 'i-ph:corners-out'}
          onClick={toggleFullScreen}
        />
      </div>
      <div ref={setPreviewElement} className="h-full min-h-0 border-t border-bolt-elements-borderColor relative flex">
        {showOverlay && <div className="absolute inset-0 z-100 bg-bolt-elements-background-depth-2" />}
        {activePreview ? (
          <>
            <div
              className={classNames(
                'flex gap-2 size-full justify-center items-center bg-bolt-elements-background-depth-2 absolute inset-0',
                {
                  'z-loading': !isReady,
                  '-z-1': isReady,
                },
              )}
            >
              <div className="i-svg-spinners:90-ring-with-bg text-bolt-elements-item-contentAccent text-lg" />
              <div className="text-bolt-elements-textTertiary">
                {framework?.name === 'expo' ? 'Building the mobile application' : 'Waiting for preview to load'}
              </div>
            </div>

            <Resizer
              enabled={isResponsiveMode}
              width={mobileWidth}
              setWidth={setMobileWidth}
              isPreviewFullscreen={isPreviewFullscreen}
            >
              <iframe
                ref={iframeRef}
                className={classNames('border-none size-full', {
                  hidden: loadState !== PreviewLoadState.Ready,
                })}
                src={iframeUrl}
                title="Bolt preview"
                allow={POLICY}
                onLoad={() => {
                  setLoadState((loadState) =>
                    loadState === PreviewLoadState.Idle
                      ? PreviewLoadState.PossiblyWaitingForConnect
                      : PreviewLoadState.Ready,
                  );
                }}
              />
            </Resizer>
          </>
        ) : (
          <div className="flex size-full justify-center items-center text-black bg-white">No preview available</div>
        )}
      </div>
    </div>
  );

  if (!activePreview || isLoadingSnapshot) {
    return <LoadingScreen />;
  }

  if (isPreviewFullscreen) {
    return createPortal(previewMarkup, previewElement);
  }

  return previewMarkup;
});

export const Preview = memo(PreviewImpl);
